import { useState, useEffect } from "react";
import { LocLabel } from "./LocationIcons";
import { IcPlus, IcBox, IcSearch, IcCart, IcPin, IcReceipt, IcChair, IcStar, ProductGlyph } from "./Icons";

// "" → relative URL: dev'de CRA proxy (package.json "proxy"), prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";

/* ── Table zone classifier ──────────────────────────────────── */
const getZone = (t) => {
  const code  = (t.code  || "").toLowerCase();
  const label = (t.label || "").toLowerCase();
  if (
    code.startsWith("dis") ||
    label.includes("bahçe") || label.includes("bahce") ||
    label.includes("teras") || label.includes("dış") ||
    label.includes("balkon")
  ) return "dis";
  return "ic";
};

/* ── Bestseller Chip ────────────────────────────────────────── */
function BsChip({ product, qty, onChange, onWeightClick }) {
  return (
    <button
      className={"mm-bs-chip" + (qty > 0 ? " in-cart" : "")}
      onClick={() => product.is_weight_based ? onWeightClick(product) : onChange(product.id, product.name, product.price, +1)}
    >
      <span className="mm-bsc-emoji"><ProductGlyph emoji={product.image_emoji} size={15} /></span>
      <span className="mm-bsc-name">{product.name}</span>
      <span className="mm-bsc-price">₺{product.price}</span>
      {qty > 0 && <span className="mm-bsc-badge">{qty}</span>}
    </button>
  );
}

/* ── Static bestseller names (priority order) ───────────────── */
const STATIC_BS = ["Çay", "Latte", "Americano", "Türk Kahvesi", "Iced Latte"];

/* ── Gramaj Giriş Modalı ────────────────────────────────────── */
function WeightInputModal({ product, onConfirm, onCancel }) {
  const [grams, setGrams] = useState("");
  const gramsNum = parseInt(grams, 10);
  const previewPrice = (!isNaN(gramsNum) && gramsNum > 0)
    ? (product.price * gramsNum / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  const handleKey = (e) => {
    if (e.key === "Enter" && gramsNum > 0) onConfirm(gramsNum);
    if (e.key === "Escape") onCancel();
  };

  return (
    <div className="mm-weight-overlay" onClick={onCancel}>
      <div className="mm-weight-dialog" onClick={e => e.stopPropagation()}>
        <div className="mm-weight-title" style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
          <ProductGlyph emoji={product.image_emoji} size={20} /> {product.name}
        </div>
        <div className="mm-weight-hint">Birim fiyat: ₺{product.price} / 100g</div>
        <div className="mm-weight-row">
          <input
            type="number"
            className="mm-weight-input"
            placeholder="Gramaj"
            value={grams}
            onChange={e => setGrams(e.target.value)}
            onKeyDown={handleKey}
            autoFocus
            min="1"
            max="10000"
          />
          <span className="mm-weight-unit">g</span>
        </div>
        {previewPrice && (
          <div className="mm-weight-preview">= ₺{previewPrice}</div>
        )}
        <div className="mm-weight-actions">
          <button className="mm-confirm-cancel" onClick={onCancel}>İptal</button>
          <button
            className="mm-confirm-ok"
            disabled={!gramsNum || gramsNum < 1}
            onClick={() => onConfirm(gramsNum)}
          >
            Sepete Ekle
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Product Card (Seçenek B: tüm kart tıklanabilir) ─────────── */
function ProductCard({ product, qty, onChange, onWeightClick }) {
  const [flash, setFlash] = useState(false);

  const handleClick = () => {
    if (product.is_weight_based) { onWeightClick(product); return; }
    onChange(product.id, product.name, product.price, +1);
    setFlash(true);
    setTimeout(() => setFlash(false), 350);
  };

  return (
    <div
      className={"mm-product-card" + (qty > 0 ? " in-cart" : "") + (flash ? " flash" : "")}
      onClick={handleClick}
    >
      <div className="mm-pc-emoji"><ProductGlyph emoji={product.image_emoji} size={22} /></div>
      <div className="mm-pc-name">{product.name}</div>
      <div className="mm-pc-price">₺{product.price}</div>
      {qty > 0 && <span className="mm-pc-qty-badge">{qty}</span>}
      <span className="mm-pc-corner-plus">+</span>
    </div>
  );
}

/* ── Confirm Order Modal ────────────────────────────────────── */
function ConfirmOrderModal({ tableName, serviceType, total, onConfirm, onCancel, submitting }) {
  return (
    <div className="mm-confirm-overlay" onClick={onCancel}>
      <div className="mm-confirm-dialog" onClick={e => e.stopPropagation()}>
        <div className="mm-confirm-icon-wrap"><IcReceipt size={36} /></div>
        <div className="mm-confirm-title">Siparişi Onayla</div>
        <p className="mm-confirm-body">
          {serviceType === "TAKEAWAY"
            ? <>Paket sipariş toplam <strong>₺{total.toLocaleString("tr-TR")}</strong> tutarında girilecektir. Emin misiniz?</>
            : <><strong>{tableName}</strong> masasına toplam{" "}<strong>₺{total.toLocaleString("tr-TR")}</strong> tutarında sipariş girilecektir. Emin misiniz?</>
          }
        </p>
        <div className="mm-confirm-actions">
          <button
            className="mm-confirm-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            İptal
          </button>
          <button
            className="mm-confirm-ok"
            onClick={onConfirm}
            disabled={submitting}
            autoFocus
          >
            {submitting ? "Gönderiliyor…" : "Onayla ve Siparişi Gir"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Modal ─────────────────────────────────────────────── */
export default function ManualOrderModal({ onClose, onSuccess }) {
  const [tables,       setTables]       = useState([]);
  const [menu,         setMenu]         = useState([]);
  const [bestsellers,  setBestsellers]  = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTablePicker, setShowTablePicker] = useState(true);   // masa seçilince küçülür
  const [showBs, setShowBs] = useState(() => (typeof window !== "undefined" ? window.innerWidth > 700 : true)); // en çok satılanlar — mobilde varsayılan kapalı
  const [cart,         setCart]         = useState({});
  const [note,         setNote]         = useState("");
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [search,       setSearch]       = useState("");
  const [activeTab,    setActiveTab]    = useState(null);
  const [location,     setLocation]     = useState("ic");
  const [serviceType,  setServiceType]  = useState("TABLE"); // "TABLE" | "TAKEAWAY"
  const [confirmOpen,  setConfirmOpen]  = useState(false);
  const [sessionMap,   setSessionMap]   = useState({});  // table_id → total_amount (TL)
  const [weightInput,  setWeightInput]  = useState(null); // product object | null

  /* Statik bestseller: menu yüklendikten sonra isimle eşleştir */
  const buildStaticBs = (cats) => {
    const all = cats.flatMap(c => c.products || []);
    return STATIC_BS
      .map(term => all.find(p => p.name.toLowerCase().includes(term.toLowerCase())))
      .filter(Boolean);
  };

  /* Fetch all data in parallel */
  useEffect(() => {
    const menuFetch    = fetch(`${API}/api/menu`).then(r => r.json());
    const tablesFetch  = fetch(`${API}/api/admin/tables`, { credentials: "include" }).then(r => r.json());
    const openFetch    = fetch(`${API}/api/sessions?status=OPEN`).then(r => r.json());
    const partialFetch = fetch(`${API}/api/sessions?status=PARTIALLY_PAID`).then(r => r.json());

    Promise.all([menuFetch, tablesFetch, openFetch, partialFetch]).then(([mRes, tRes, openRes, partialRes]) => {
      const cats = mRes.categories || [];
      setMenu(cats);
      setBestsellers(buildStaticBs(cats));
      if (cats.length > 0) setActiveTab(cats[0].id);
      setTables((tRes.tables || []).filter(t => t.is_active));

      const allSessions = [...(openRes.sessions || []), ...(partialRes.sessions || [])];
      const map = {};
      allSessions.forEach(s => { map[s.table_id] = s.total_amount; });
      setSessionMap(map);

      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line

  /* ESC: önce confirm modalı kapat, yoksa ana modalı kapat */
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") {
        if (confirmOpen) setConfirmOpen(false);
        else onClose();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [confirmOpen]); // eslint-disable-line

  /* Cart helpers */
  const changeQty = (productId, productName, productPrice, delta) => {
    setCart(prev => {
      const cur  = prev[productId]?.qty || 0;
      const next = cur + delta;
      if (next <= 0) { const { [productId]: _, ...rest } = prev; return rest; }
      return { ...prev, [productId]: { product_name: productName, product_price: productPrice, qty: next } };
    });
  };

  const addWeightItem = (product, grams) => {
    setCart(prev => ({
      ...prev,
      [product.id]: {
        product_name:    product.name,
        product_price:   product.price,
        qty:             1,
        weight_grams:    grams,
        is_weight_based: true,
      },
    }));
    setWeightInput(null);
  };

  const removeCartItem = (productId) => {
    setCart(prev => { const { [productId]: _, ...rest } = prev; return rest; });
  };

  const total     = Object.values(cart).reduce((s, i) => s + (i.is_weight_based ? i.weight_grams * i.product_price / 100 : i.qty * i.product_price), 0);
  const itemCount = Object.values(cart).reduce((s, i) => s + (i.is_weight_based ? 1 : i.qty), 0);

  const submit = async () => {
    if (serviceType === "TABLE" && !selectedTable) return;
    if (itemCount === 0) return;
    setSubmitting(true);
    const items = Object.entries(cart).map(([id, v]) => {
      const item = { product_id: Number(id), quantity: v.qty };
      if (v.is_weight_based) item.weight_grams = v.weight_grams;
      return item;
    });
    const body = serviceType === "TAKEAWAY"
      ? { service_type: "TAKEAWAY", items, note: note || null, skip_queue: true, client_idempotency_key: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }
      : { table_code: selectedTable.code, items, note: note || null, skip_queue: true, client_idempotency_key: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
    try {
      const res = await fetch(`${API}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 403) {
          alert("Sipariş alma aktif olmadığından manuel sipariş girilememektedir.");
        } else {
          alert(data.detail || "Sipariş gönderilemedi");
        }
        setSubmitting(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      onSuccess(serviceType === "TAKEAWAY" ? { takeaway_session_id: data.session_id } : undefined);
      onClose();
    } catch {
      alert("Sipariş gönderilemedi");
    }
    setSubmitting(false);
  };

  /* Derived */
  const filteredTables  = tables.filter(t => getZone(t) === location);
  const searchTrimmed   = search.trim().toLowerCase();
  const searchResults   = searchTrimmed
    ? menu.flatMap(cat => cat.products.filter(p => p.name.toLowerCase().includes(searchTrimmed)))
    : [];
  const activeCategory  = menu.find(c => c.id === activeTab);
  const tabProducts     = activeCategory ? activeCategory.products : [];
  const displayProducts = searchTrimmed ? searchResults : tabProducts;

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="modal-overlay">
      <div className="manual-modal">

        {/* ── Header ── */}
        <div className="mm-header">
          <div className="mm-title"><IcPlus size={18} /> Manuel Sipariş</div>
          <div className="mm-header-toggle">
            <button
              className={"mm-svc-btn" + (serviceType === "TABLE" ? " active" : "")}
              onClick={() => { setServiceType("TABLE"); setSelectedTable(null); }}
            ><span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcChair size={14} /> Masa</span></button>
            <button
              className={"mm-svc-btn" + (serviceType === "TAKEAWAY" ? " takeaway-active" : "")}
              onClick={() => { setServiceType("TAKEAWAY"); setSelectedTable(null); }}
            ><span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcBox size={14} /> Paket</span></button>
          </div>
          <button className="mm-close" onClick={onClose}>✕</button>
        </div>

        {loading ? <div className="mm-loading">Yükleniyor…</div> : (
          <>

            {/* ── Table Section (yalnızca Masa modunda) ── */}
            {serviceType === "TABLE" && (
              <div className="mm-table-section">
                {selectedTable && !showTablePicker ? (
                  /* Kompakt: seçili masa + değiştir (alan kazanır) */
                  <button className="mm-table-selected" onClick={() => setShowTablePicker(true)}>
                    <span className="mm-ts-label"><IcPin size={14} /> {selectedTable.label}</span>
                    <span className="mm-ts-change">Değiştir</span>
                  </button>
                ) : (
                  <>
                    <div className="mm-loc-tabs">
                      <button
                        className={"mm-loc-tab" + (location === "ic" ? " active" : "")}
                        onClick={() => { setLocation("ic"); setSelectedTable(null); }}
                      ><LocLabel type="ic" size={15}>İç Mekan</LocLabel></button>
                      <button
                        className={"mm-loc-tab" + (location === "dis" ? " active" : "")}
                        onClick={() => { setLocation("dis"); setSelectedTable(null); }}
                      ><LocLabel type="dis" size={15}>Dış Mekan</LocLabel></button>
                    </div>
                    <div className="mm-table-strip">
                      {filteredTables.length === 0
                        ? <span className="mm-no-tables">Bu bölümde masa bulunamadı</span>
                        : filteredTables.map(t => {
                            const tableTotal = sessionMap[t.id];
                            return (
                              <button
                                key={t.id}
                                className={"mm-table-chip" + (selectedTable?.id === t.id ? " selected" : "") + (tableTotal != null ? " has-session" : "")}
                                onClick={() => { setSelectedTable(t); setShowTablePicker(false); }}
                              >
                                <span className="mm-chip-label">{t.label}</span>
                                {tableTotal != null && (
                                  <span className="mm-chip-amount">₺{Math.round(tableTotal)}</span>
                                )}
                              </button>
                            );
                          })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Main: two-column ── */}
            <div className="mm-main">

              {/* LEFT: Products */}
              <div className="mm-products-pane">

                {/* Search */}
                <div className="mm-search-wrap">
                  <span className="mm-search-icon"><IcSearch size={15} /></span>
                  <input
                    className="mm-search-input"
                    placeholder="Ürün ara…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="mm-search-clear" onClick={() => setSearch("")}>✕</button>
                  )}
                </div>

                {/* Bestsellers strip — isteğe bağlı açılır (mobilde varsayılan kapalı, alan kazanır) */}
                {!searchTrimmed && bestsellers.length > 0 && (
                  <div className={"mm-bs-strip" + (showBs ? " open" : "")}>
                    <button className="mm-bs-toggle" onClick={() => setShowBs(v => !v)}>
                      <IcStar size={13} />
                      <span>En çok satılanlar</span>
                      <svg className={"mm-bs-chev" + (showBs ? " open" : "")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                    {showBs && (
                      <div className="mm-bs-row">
                        {bestsellers.map(p => (
                          <BsChip
                            key={p.id}
                            product={p}
                            qty={cart[p.id]?.qty || 0}
                            onChange={changeQty}
                            onWeightClick={setWeightInput}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Category tabs */}
                {!searchTrimmed && (
                  <div className="mm-cat-tabs">
                    {menu.map(cat => (
                      <button
                        key={cat.id}
                        className={"mm-cat-tab" + (activeTab === cat.id ? " active" : "")}
                        onClick={() => setActiveTab(cat.id)}
                      >{cat.emoji} {cat.name}</button>
                    ))}
                  </div>
                )}

                {/* Product grid */}
                <div className="mm-product-grid">
                  {displayProducts.length === 0
                    ? <div className="mm-empty">{searchTrimmed ? "Ürün bulunamadı" : "Bu kategoride ürün yok"}</div>
                    : displayProducts.map(p => (
                        <ProductCard
                          key={p.id}
                          product={p}
                          qty={cart[p.id]?.qty || 0}
                          onChange={changeQty}
                          onWeightClick={setWeightInput}
                        />
                      ))}
                </div>
              </div>

              {/* RIGHT: Cart Panel */}
              <div className="mm-cart-pane">
                <div className="mm-cart-header">
                  <span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcCart size={15} /> Sepet</span>
                  {serviceType === "TAKEAWAY"
                    ? <span className="mm-cart-table" style={{display:"inline-flex",alignItems:"center",gap:6}}><IcBox size={13} /> Paket</span>
                    : selectedTable
                      ? <span className="mm-cart-table" style={{display:"inline-flex",alignItems:"center",gap:6}}><IcPin size={13} /> {selectedTable.label}</span>
                      : <span className="mm-cart-no-table">Masa seçilmedi</span>}
                </div>

                <div className="mm-cart-list">
                  {itemCount === 0 ? (
                    <div className="mm-cart-empty">Henüz ürün eklenmedi</div>
                  ) : (
                    Object.entries(cart).map(([id, v]) => (
                      <div key={id} className="mm-cart-item">
                        <div className="mm-ci-name">{v.product_name}</div>
                        {v.is_weight_based ? (
                          <div className="mm-ci-row">
                            <span className="mm-ci-weight-badge">{v.weight_grams}g</span>
                            <button
                              className="mm-ci-btn"
                              title="Gramajı düzenle"
                              onClick={() => setWeightInput({ id: Number(id), name: v.product_name, price: v.product_price, image_emoji: "" })}
                            >✎</button>
                            <button
                              className="mm-ci-btn minus"
                              onClick={() => removeCartItem(Number(id))}
                            >✕</button>
                            <span className="mm-ci-price">₺{(v.weight_grams * v.product_price / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ) : (
                          <div className="mm-ci-row">
                            <div className="mm-ci-controls">
                              <button className="mm-ci-btn minus" onClick={() => changeQty(Number(id), v.product_name, v.product_price, -1)}>−</button>
                              <span className="mm-ci-qty">{v.qty}</span>
                              <button className="mm-ci-btn plus"  onClick={() => changeQty(Number(id), v.product_name, v.product_price, +1)}>+</button>
                            </div>
                            <span className="mm-ci-price">₺{(v.qty * v.product_price).toLocaleString("tr-TR")}</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {itemCount > 0 && (
                  <div className="mm-cart-total">
                    <span className="mm-ct-label">{itemCount} ürün</span>
                    <span className="mm-ct-amount">₺{total.toLocaleString("tr-TR")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Bottom Bar ── */}
            <div className="mm-bottom-bar">
              <textarea
                className="mm-note mm-bb-note"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Sipariş notu… (opsiyonel)"
                rows={1}
              />
              <button
                className="mm-submit"
                disabled={(serviceType === "TABLE" && !selectedTable) || itemCount === 0 || submitting}
                onClick={() => setConfirmOpen(true)}
              >
                {submitting
                  ? "Gönderiliyor…"
                  : itemCount > 0
                    ? `${serviceType === "TAKEAWAY" ? "Paket Siparişi Gir" : "Siparişi Gir"} · ₺${total.toLocaleString("tr-TR")} ✓`
                    : "Siparişi Gir ✓"}
              </button>
            </div>
          </>
        )}

        {/* ── Gramaj Giriş Modalı ── */}
        {weightInput && (
          <WeightInputModal
            product={weightInput}
            onConfirm={(grams) => addWeightItem(weightInput, grams)}
            onCancel={() => setWeightInput(null)}
          />
        )}

        {/* ── Confirm Overlay (ana modal üzerinde) ── */}
        {confirmOpen && (
          <ConfirmOrderModal
            tableName={selectedTable?.label}
            serviceType={serviceType}
            total={total}
            submitting={submitting}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={async () => {
              await submit();
              setConfirmOpen(false);
            }}
          />
        )}

      </div>
    </div>
  );
}
